import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Button,
    Chip,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import WarningIcon from '@mui/icons-material/ReportProblem';
import CheckIcon from '@mui/icons-material/CheckCircle';
import { ACCENTS } from '../../lib/theme';
import { formatInstantAu } from '../../lib/time';
import { fetchSwms, signSwms, type SwmsForJob, type SwmsTaskCard } from '../../lib/employee';
import { SignaturePad, type SignaturePadHandle } from '../../components/employee/SignaturePad';
import { Toast, type ToastMessage } from '../../components/Toast';
import { EmployeeShell, FullWidthButton } from './shell';

/**
 * SWMS sign-off — a port of `swms_splash.php` and `add_swms.php`.
 *
 * Three steps, the same three the legacy flow had: a splash saying why you are
 * here, the tasks, then the signature. What changes:
 *
 *   * RISK COLOUR IS DERIVED. The legacy cards coloured themselves from
 *     `colour_background` and `colour_text` on the task row — both NULL on all
 *     20 tasks in production, so every card rendered with no colour and a
 *     `[0]` substring of the risk label as its icon. Here the colour comes
 *     from the risk level, so a Major task cannot be made to look minor.
 *
 *   * ONE CARD AT A TIME, counted in the header. The legacy page rendered every
 *     task into the DOM at once and revealed them five at a time with
 *     `.slice(currentIndex, currentIndex + 5)`, which on a 20-task job is four
 *     taps through dense blocks of text with no sense of how many remain.
 *
 *   * THE SUBMIT BUTTON WAITS FOR BOTH. The legacy Next button appeared as soon
 *     as the person-responsible box had any text, and the signature was posted
 *     as whatever `signaturePad.toDataURL()` returned — including a blank
 *     canvas, which is a signed safety document with no signature on it.
 *
 * The shift has already started by the time anyone lands here. That is
 * deliberate and matches the legacy flow: the clock records who was on site,
 * and holding it hostage to a form would lose that.
 */

type Step = 'splash' | 'tasks' | 'sign';

const RISK: Readonly<Record<number, { label: string; colour: string }>> = {
    1: { label: 'Major', colour: ACCENTS.red },
    2: { label: 'Moderate', colour: ACCENTS.amber },
    3: { label: 'Minor', colour: ACCENTS.teal },
};

function riskOf(level: number | null): { label: string; colour: string } {
    return RISK[level ?? 0] ?? { label: 'Unrated', colour: ACCENTS.blue };
}

function TaskCard({ task }: { task: SwmsTaskCard }): React.JSX.Element {
    const risk = riskOf(task.riskLevel);
    return (
        <Paper
            sx={{
                p: 2,
                borderRadius: 3,
                border: `2px solid ${alpha(risk.colour, 0.5)}`,
                borderLeftWidth: 6,
                bgcolor: 'background.paper',
            }}
        >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <WarningIcon sx={{ color: risk.colour }} />
                <Typography sx={{ flex: 1, fontWeight: 800, fontSize: '1.05rem', lineHeight: 1.25 }}>
                    {task.task}
                </Typography>
                <Chip
                    size="small"
                    label={risk.label}
                    sx={{
                        bgcolor: alpha(risk.colour, 0.2),
                        color: risk.colour,
                        fontWeight: 700,
                        flexShrink: 0,
                    }}
                />
            </Stack>

            <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: risk.colour, textTransform: 'uppercase', letterSpacing: 0.4 }}
            >
                What are the hazards and risks?
            </Typography>
            {/* pre-wrap, because these are paragraphed lists — 89 line breaks
                across the 20 tasks. Collapsing them makes a run-on sentence. */}
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2, mt: 0.5 }}>
                {task.hazardsRisks ?? '—'}
            </Typography>

            <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: risk.colour, textTransform: 'uppercase', letterSpacing: 0.4 }}
            >
                How will they be controlled?
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                {task.controlMeasures ?? '—'}
            </Typography>
        </Paper>
    );
}

export function MySwms(): React.JSX.Element {
    const { jobId: jobIdParam } = useParams<{ jobId: string }>();
    const jobId = Number(jobIdParam);
    const navigate = useNavigate();

    const [swms, setSwms] = useState<SwmsForJob | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [step, setStep] = useState<Step>('splash');
    const [index, setIndex] = useState(0);
    const [responsible, setResponsible] = useState('');
    const [hasInk, setHasInk] = useState(false);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState<ToastMessage | null>(null);
    const padRef = useRef<SignaturePadHandle | null>(null);

    useEffect(() => {
        if (!Number.isInteger(jobId)) {
            setError('Invalid job');
            setLoading(false);
            return;
        }
        let active = true;
        fetchSwms(jobId)
            .then((data) => {
                if (active) {
                    setSwms(data);
                    setError(null);
                }
            })
            .catch((err: unknown) => {
                if (active) {
                    setError(err instanceof Error ? err.message : String(err));
                }
            })
            .finally(() => {
                if (active) {
                    setLoading(false);
                }
            });
        return () => {
            active = false;
        };
    }, [jobId]);

    async function onSubmit(): Promise<void> {
        const signature = padRef.current?.toDataUrl() ?? null;
        if (signature === null) {
            setToast({ message: 'Sign in the box before submitting', severity: 'warning' });
            return;
        }
        setSaving(true);
        try {
            await signSwms(jobId, { personResponsible: responsible.trim(), signature });
            void navigate('/my', { replace: true });
        } catch (err: unknown) {
            setToast({
                message: err instanceof Error ? err.message : 'Could not save your SWMS',
                severity: 'error',
            });
            setSaving(false);
        }
    }

    // No spinner — an empty shell until the SWMS arrives.
    if (loading) {
        return <EmployeeShell title="SWMS" backTo="/my" />;
    }

    if (swms === null) {
        return (
            <EmployeeShell title="SWMS" backTo="/my">
                <Typography color="error">{error ?? 'That job could not be found'}</Typography>
            </EmployeeShell>
        );
    }

    // ─── Splash ─────────────────────────────────────────────────────────────
    if (step === 'splash') {
        const alreadySigned = !swms.status.required;
        return (
            <EmployeeShell title="SWMS" subtitle={swms.clientName ?? undefined} backTo="/my">
                <Paper
                    sx={{
                        p: 3,
                        borderRadius: 3,
                        textAlign: 'center',
                        border: `2px solid ${alpha(alreadySigned ? ACCENTS.green : ACCENTS.amber, 0.5)}`,
                        bgcolor: 'background.paper',
                    }}
                >
                    {alreadySigned ? (
                        <CheckIcon sx={{ fontSize: 48, color: ACCENTS.green }} />
                    ) : (
                        <WarningIcon sx={{ fontSize: 48, color: ACCENTS.amber }} />
                    )}
                    <Typography variant="h5" fontWeight={800} sx={{ mt: 1 }}>
                        Safe Work Method Statement
                    </Typography>
                    <Typography sx={{ mt: 1.5, fontWeight: 600 }}>{swms.address}</Typography>
                    <Typography color="text.secondary" sx={{ mt: 1.5 }}>
                        {swms.status.reason === 'new'
                            ? 'No SWMS recorded for you on this job.'
                            : swms.status.reason === 'expired'
                              ? 'Your SWMS has expired. Please complete another one.'
                              : `Signed on ${swms.status.lastSignedAt === null ? '—' : formatInstantAu(swms.status.lastSignedAt)}. You can sign again if anything has changed.`}
                    </Typography>

                    {swms.tasks.length === 0 ? (
                        <Typography color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
                            No SWMS tasks are attached to this job yet — tell the office before starting work.
                        </Typography>
                    ) : (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                            {swms.tasks.length} task{swms.tasks.length === 1 ? '' : 's'} to read
                        </Typography>
                    )}

                    <FullWidthButton
                        colour={alreadySigned ? ACCENTS.green : ACCENTS.amber}
                        disabled={swms.tasks.length === 0}
                        onClick={() => {
                            setStep('tasks');
                        }}
                        sx={{ mt: 2 }}
                    >
                        Start
                    </FullWidthButton>
                    <Button
                        fullWidth
                        onClick={() => {
                            void navigate('/my');
                        }}
                        sx={{ mt: 1, textTransform: 'none' }}
                    >
                        Not now
                    </Button>
                </Paper>
                <Toast
                    placement="centre"
                    toast={toast}
                    onClose={() => {
                        setToast(null);
                    }}
                />
            </EmployeeShell>
        );
    }

    // ─── Tasks, one at a time ───────────────────────────────────────────────
    if (step === 'tasks') {
        const task = swms.tasks[index];
        const last = index >= swms.tasks.length - 1;
        return (
            <EmployeeShell
                title="SWMS"
                subtitle={`${String(index + 1)} of ${String(swms.tasks.length)}`}
                backTo="/my"
            >
                {task !== undefined ? <TaskCard task={task} /> : null}

                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                    <FullWidthButton
                        colour={ACCENTS.blue}
                        disabled={index === 0}
                        onClick={() => {
                            setIndex((i) => Math.max(0, i - 1));
                        }}
                    >
                        Back
                    </FullWidthButton>
                    <FullWidthButton
                        colour={last ? ACCENTS.green : ACCENTS.blue}
                        onClick={() => {
                            if (last) {
                                setStep('sign');
                            } else {
                                setIndex((i) => i + 1);
                                window.scrollTo({ top: 0 });
                            }
                        }}
                    >
                        {last ? 'Sign' : 'Next'}
                    </FullWidthButton>
                </Stack>
                <Toast
                    placement="centre"
                    toast={toast}
                    onClose={() => {
                        setToast(null);
                    }}
                />
            </EmployeeShell>
        );
    }

    // ─── Signature ──────────────────────────────────────────────────────────
    const ready = responsible.trim() !== '' && hasInk;
    return (
        <EmployeeShell title="Sign the SWMS" subtitle={swms.address} backTo="/my">
            <Paper sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="Name & position of the person responsible"
                    placeholder="e.g. Sam Taylor, Site Supervisor"
                    value={responsible}
                    onChange={(e) => {
                        setResponsible(e.target.value);
                    }}
                    helperText="Who is ensuring compliance with this SWMS"
                    sx={{ mb: 2 }}
                />

                <SignaturePad handleRef={padRef} onChange={setHasInk} />

                <Typography variant="body2" sx={{ mt: 2, fontWeight: 600 }}>
                    I have read and understood this SWMS.
                </Typography>

                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                    <FullWidthButton
                        colour={ACCENTS.blue}
                        disabled={saving}
                        onClick={() => {
                            setStep('tasks');
                        }}
                    >
                        Back
                    </FullWidthButton>
                    <FullWidthButton
                        colour={ACCENTS.green}
                        disabled={!ready || saving}
                        startIcon={<CheckIcon />}
                        onClick={() => void onSubmit()}
                    >
                        {saving ? 'Submitting…' : 'Submit'}
                    </FullWidthButton>
                </Stack>
            </Paper>
            <Toast
                placement="centre"
                toast={toast}
                onClose={() => {
                    setToast(null);
                }}
            />
        </EmployeeShell>
    );
}
